/**
 *
- Вставка картинок поштучно
- Правки в генератор для избегани надписей в видео

- Пометки на фреймы которым надо перегенерировать видео заново
- Пометки на фреймы у которых видео уже готово и они годны для продакшена
- Пометки на фреймы которые надо временно исключить из генерации видео
- UI просмотра видео и возможности задать опции обрезки, реверса, ускорения
 */
import { Button, message, Skeleton, Switch } from 'antd';
import gql from 'graphql-tag';
import { useAuthedMutation } from 'utils/qlAuth';
import React from 'react';
import { StoryFramesModel } from 'interface/models';
import { TimelinePlan_OneFrameRow } from './TimelinePlan_OneFrameRow';
import { useStoryFramesQuery } from 'queries/ai-threshold';
import { BottomButtons } from 'components/SharedComponents/BottomButtons/BottomButtons';
import { TimelineBuildVideoComponent } from './TimelineBuildVideo';
import { ReactSortable } from 'react-sortablejs';

export const TimelinePlan = (props: { qStory: any; storyId: number }) => {
  const qFrames = useStoryFramesQuery(props.storyId);

  const [updateFrame] = useAuthedMutation(
    gql`
      mutation StoryController_updateFrame($frameId: Int!, $input: JSON!) {
        StoryController_updateFrame(frameId: $frameId, config: $input) {
          id
        }
      }
    `,
  );

  const [frames, setFrames] = React.useState<StoryFramesModel[]>([]);
  const [displayType, setDisplayType] = React.useState<string>('medium');
  const [hideHidden, setHideHidden] = React.useState<boolean>(true);
  const [orderMode, setOrderMode] = React.useState<boolean>(false);
  const [needSave, setNeedSave] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (qFrames.data?.FrameController_getStoryFrames) {
      setFrames(qFrames.data.FrameController_getStoryFrames);
    }
  }, [qFrames.data]);

  if (qFrames.error) {
    return <div>error</div>;
  }
  if (qFrames.loading) {
    return <Skeleton active />;
  }

  const onFrameChange = async (newFrame: StoryFramesModel) => {
    setFrames(prevFrames => prevFrames.map(frame => (frame.id === newFrame.id ? { ...newFrame } : frame)));
  };

  const setNewTimeStart = async (frameId: number, newTimeStart: number) => {
    await updateFrame({
      variables: {
        frameId,
        input: { timeStart: newTimeStart },
      },
    });
    setFrames(prevFrames => prevFrames.map(frame => (frame.id === frameId ? { ...frame, timeStart: newTimeStart } : frame)));
  };

  const sortedFrames = frames
    .filter(frame => frame.videos && frame.videos.length > 0)
    .filter(frame => !(['hidden', 'skip'].includes(frame.status) && hideHidden))
    .sort((a, b) => {
      const t = (a.timeStart || 0) - (b.timeStart || 0);
      if (t !== 0) {
        return t;
      }
      return a.id - b.id;
    });

  for (let i = 0; i < sortedFrames.length; i++) {
    if (!sortedFrames[i].timeStart && i > 0) {
      sortedFrames[i].timeStart = sortedFrames[i - 1].timeStart + (sortedFrames[i - 1].timeLength || 3000);
    }
  }

  const saveNewFrameOrder = async () => {
    const prom = [];
    // Сохраняем новый порядок кадров на сервере
    for (let i = 0; i < sortedFrames.length; i++) {
      const frame = sortedFrames[i];
      const newTime = i * 5000; // Пример: устанавливаем время начала каждого кадра с шагом 5 секунд

      // if (newTime !== sortedFrames[i].timeStart) {
      sortedFrames[i].timeStart = newTime;
      prom.push(
        updateFrame({
          variables: {
            frameId: frame.id,
            input: { timeStart: newTime }, // или используйте index, если поле называется иначе
          },
        }),
      );
      // }
    }

    await Promise.all(prom);
    setNeedSave(false);
    message.success('Order saved successfully');
  };

  const onSort = async (newFrames: StoryFramesModel[]) => {
    // Сохраняем новый порядок кадров на сервере
    for (let i = 0; i < newFrames.length; i++) {
      const newTime = i * 5000; // Пример: устанавливаем время начала каждого кадра с шагом 5 секунд
      newFrames[i].timeStart = newTime;
    }
    setFrames(newFrames);
    setNeedSave(true);
  };

  return (
    <>
      <BottomButtons extra={<TimelineBuildVideoComponent qStory={props.qStory} storyId={Number(props.storyId)} />}>
        <Button size="small" onClick={() => setDisplayType('small')} type={displayType === 'small' ? 'primary' : 'default'}>
          Small
        </Button>
        <Button size="small" onClick={() => setDisplayType('medium')} type={displayType === 'medium' ? 'primary' : 'default'}>
          Medium
        </Button>
        <Button size="small" onClick={() => setDisplayType('big')} type={displayType === 'big' ? 'primary' : 'default'}>
          Big
        </Button>
        <Switch
          title="Показывать скрытые фреймы"
          checked={hideHidden}
          onChange={checked => setHideHidden(checked)}
          unCheckedChildren="Show hidden"
          checkedChildren="Hide hidden"
        />
        <Switch
          title="Активировать режим сортировки перетаскиванием"
          checked={orderMode}
          onChange={checked => setOrderMode(checked)}
          unCheckedChildren="Order off"
          checkedChildren="Order mode"
        />

        <Button type={needSave ? 'primary' : 'default'} size="small" onClick={saveNewFrameOrder}>
          Save Order
        </Button>
      </BottomButtons>

      <ReactSortable list={sortedFrames} multiDrag swap setList={onSort} animation={200} handle=".sortable-handle">
        {(sortedFrames || []).map((item, index) => {
          const next = sortedFrames[index + 1];
          const prev = sortedFrames[index - 1];
          return (
            <span
              className={orderMode ? 'sortable-handle' : ''}
              style={
                orderMode
                  ? {
                      cursor: 'grab',
                      userSelect: 'none',
                    }
                  : {}
              }
              title="Перетащите для сортировки"
            >
              <TimelinePlan_OneFrameRow
                current={item}
                key={item.id}
                prev={prev}
                next={next}
                onChange={onFrameChange}
                displayType={displayType}
                setNewTimeStart={setNewTimeStart}
                qFrames={qFrames}
                storyId={props.storyId}
              />
            </span>
          );
        })}
      </ReactSortable>
    </>
  );
};
